/**
 * StandardPage - One wrapper to rule them all!
 * 
 * Instead of repeating DashboardLayout + DashboardNavbar + Footer + ArgonBox 
 * in every single page, just use this wrapper.
 * 
 * BEFORE: 15 lines of boilerplate in each file
 * AFTER: 1 line wrapper
 */

import { useState } from "react";
import PropTypes from "prop-types";
import { Alert, CircularProgress } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

function StandardPage({ 
  title, 
  subtitle, 
  children, 
  loading = false, 
  error = null,
  padding = 3,
  showHeader = true,
  headerActions = null,
  ...rest 
}) {
  
  // Show loading state
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <Alert severity="error">
            {typeof error === "string" ? error : "An error occurred"}
          </Alert>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout {...rest}>
      <DashboardNavbar />
      <ArgonBox py={padding}>
        {showHeader && (title || subtitle) && (
          <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
            <ArgonBox>
              {title && (
                <ArgonTypography variant="h5" fontWeight="bold" mb={0.5}>
                  {title}
                </ArgonTypography>
              )}
              {subtitle && (
                <ArgonTypography variant="body2" color="text" sx={{ fontSize: '0.85rem' }}>
                  {subtitle}
                </ArgonTypography>
              )}
            </ArgonBox>
            {headerActions && (
              <ArgonBox>
                {headerActions}
              </ArgonBox>
            )}
          </ArgonBox>
        )}
        {children}
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

StandardPage.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  padding: PropTypes.number,
  showHeader: PropTypes.bool,
  headerActions: PropTypes.node,
};

export default StandardPage;